import { useCallback, useState } from 'react';

import { CognitoUserSession } from 'amazon-cognito-identity-js';
import { LDUser } from 'launchdarkly-js-client-sdk';
import { cloneDeepWith, isPlainObject, pickBy } from 'lodash-es';
import { useIntl } from 'react-intl';
import uuid from 'uuid';

import { DeepPartial } from '@rbi-ctg/frontend';
import {
  ISocialLoginInput,
  IsoCountryCode,
  ProviderType,
  useCreateOtpMutation,
  useSignInJwtMutation,
  useSignUpMutation,
  useSocialLoginMutation,
  useValidateAuthJwtMutation,
  useValidateOtpMutation,
} from 'generated/graphql-gateway';
import { useNavigation } from 'hooks/navigation/use-navigation';
import { useRoute } from 'hooks/navigation/use-route';
import { ModalCb } from 'hooks/use-error-modal';
import { useOtpFeature } from 'hooks/use-otp-feature';
import {
  signOut as cognitoSignOut,
  signUp as cognitoSignUp,
  validateLogin as cognitoValidateLogin,
} from 'remote/auth/cognito';
import { useFlag, useLDContext } from 'state/launchdarkly';
import { useMParticleContext } from 'state/mParticle';
import { SignInPhases } from 'state/mParticle/constants';
import AuthStorage from 'utils/cognito/storage';
import { platform, welcomeEmailDomain } from 'utils/environment';
import { parseGraphQLErrorCodes } from 'utils/errors';
import { GraphQLErrorCodes } from 'utils/errors/types';
import { LaunchDarklyFlag } from 'utils/launchdarkly';
import LocalStorage, { StorageKeys } from 'utils/local-storage';
import { OTPAuthDeliveryMethod, isOTPEmailEnabled, isOTPSMSEnabled } from 'utils/otp';
import { routes } from 'utils/routing';

import { SIGN_IN_FAIL } from '../constants';
import { JwtValidationError, OtpValidationError, UserNotFoundError } from '../errors';
import { ModalAuthState } from '../types';

import { useThirdPartyAuthentication } from './use-third-party-authentication';

const NON_SESSION_SPECIFIC_STORAGE_KEYS = [
  StorageKeys.LANGUAGE,
  StorageKeys.REGION,
  StorageKeys.LAST_TIME_COOKIES_ACCEPTED,
];

export interface IUseAccountAuthentication {
  refreshCurrentUser(): Promise<void>;
  openErrorDialog: ModalCb;
  setCurrentUser(session: null | CognitoUserSession): void;
  setModalAuthState(modalAuthState: ModalAuthState): void;
  modalAuthIsOpen: boolean;
}

interface INavigateOnSuccessParams {
  email: string;
}

interface ISignInNavigation {
  navigateOnSuccess?: boolean;
  navigateOnSuccessParams?: INavigateOnSuccessParams;
}

interface ISignInUserParams {
  email?: string;
  phoneNumber?: string;
  authenticationMethod?: string;
  socialAuthenticationType?: ProviderType;
}

export type ISignIn = ISignInNavigation & ISignInUserParams;

interface IFavoriteStore {
  storeId?: string;
  storeNumber: string;
}

interface ISignUp {
  email: string;
  name: string;
  dob?: string;
  phoneNumber: string;
  country: string;
  wantsPromotionalEmails: boolean;
  zipcode?: string;
  favoriteStores?: IFavoriteStore[];
}

export interface ISignUpResult {
  jwt: string | null | undefined;
}

interface IValidateLogin {
  jwt: string;
  username: string;
}

interface IGetSesionIdAndChallengeCodeOtp {
  email?: string;
  phoneNumber?: string;
  otpCode: string;
  sessionId: string;
}

type IStoreOtpCredentials = { sessionId: string } & ISignInUserParams;

export const getStoredOtpCredentials = () => LocalStorage.getItem(StorageKeys.OTP);
export const storeOtpCredentials = (data: IStoreOtpCredentials) =>
  LocalStorage.setItem(StorageKeys.OTP, data);

export const useAccountAuthentication = ({
  refreshCurrentUser,
  openErrorDialog,
  setCurrentUser,
  setModalAuthState,
  modalAuthIsOpen,
}: IUseAccountAuthentication) => {
  const { formatMessage } = useIntl();
  const { attemptGetUpdatedLdFlag } = useLDContext();
  const {
    signInEvent: mParticleSignInEvent,
    signOutEvent: mParticleSignOutEvent,
    signUpEvent: mParticleSignUpEvent,
  } = useMParticleContext();
  const { logUserOutOfThirdPartyServices } = useThirdPartyAuthentication();
  const enableSignUpInBE = useFlag(LaunchDarklyFlag.ENABLE_COGNITO_SIGNUP_IN_BE);
  const enableUserNotFoundMaskAuthFlow = useFlag(
    LaunchDarklyFlag.ENABLE_USER_NOT_FOUND_MASK_ON_AUTH_FLOW
  );
  const { enableOtpFlagValue } = useOtpFeature();
  const preloaded = LocalStorage.getItem(StorageKeys.AUTH_REDIRECT) || {};
  const [originLocation, setOriginLoc] = useState<null | string>(preloaded.callbackUrl || null);
  const { navigate } = useNavigation();
  const { params: routeParams } = useRoute();
  const [validateLoginIsLoading, setValidateLoginIsLoading] = useState<boolean>(false);

  // Track sign in otp method separatly from `enableOtpFlagValue` so that changes
  // in `enableOtpFlagValue` does not affect the sign in method used.
  const [signInOtpMethod, setSignInOtpMethod] = useState<OTPAuthDeliveryMethod>(enableOtpFlagValue);

  const [signInJwtMutation, { loading: signInMutationLoading }] = useSignInJwtMutation();
  const [socialLoginMutation, { loading: socialLoginMutationLoading }] = useSocialLoginMutation();
  const [
    validateAuthJwtMutation,
    { loading: validateAuthMutationLoading },
  ] = useValidateAuthJwtMutation();
  const [createOtpMutation, { loading: createOtpMutationLoading }] = useCreateOtpMutation();
  const [validateOtpMutation, { loading: validateOtpMutationLoading }] = useValidateOtpMutation();
  const [signUpMutation] = useSignUpMutation();

  const getSessionIdAndChallengeCodeOtp = useCallback(
    async ({ email, phoneNumber, otpCode, sessionId }: IGetSesionIdAndChallengeCodeOtp) => {
      const { data } = await validateOtpMutation({
        variables: {
          input: {
            code: otpCode,
            email,
            phoneNumber,
            sessionId,
          },
        },
      });

      const { sessionId: validatedSessionId, challengeCode, email: validatedEmail } =
        data?.exchangeOTPCodeForCognitoCredentials ?? {};

      if (!validatedSessionId || !challengeCode) {
        throw new OtpValidationError('GraphQL validation failed');
      }

      return { sessionId: validatedSessionId, code: challengeCode, email: validatedEmail };
    },
    [validateOtpMutation]
  );

  const getSessionIdAndChallengeCode = useCallback(
    async (jwt: string) => {
      try {
        const { data } = await validateAuthJwtMutation({
          variables: {
            input: { jwt },
          },
        });
        const { sessionId, challengeCode } = data?.validateAuthJwt ?? {};
        if (!sessionId || !challengeCode) {
          throw new JwtValidationError();
        }

        return { sessionId, code: challengeCode };
      } catch (error) {
        // @ts-expect-error TS(2571) FIXME: Object is of type 'unknown'.
        if (error?.originalError?.[0]?.message?.toLowerCase() === 'email not registered') {
          throw new UserNotFoundError();
        }
        throw error;
      }
    },
    [validateAuthJwtMutation]
  );

  const signOut = useCallback(async () => {
    try {
      await cognitoSignOut();
      setCurrentUser(null);

      mParticleSignOutEvent(true);
      logUserOutOfThirdPartyServices();

      // Once we have namespaced auth storage for all platforms we can remove the excluded
      // keys from here but for now we need to make sure we don't wipe all LocalStorage
      LocalStorage.clear({ excludeKeys: NON_SESSION_SPECIFIC_STORAGE_KEYS });
    } catch (error) {
      // @ts-expect-error TS(2571) FIXME: Object is of type 'unknown'.
      mParticleSignOutEvent(false, error.message);
      refreshCurrentUser();
      throw error;
    }
  }, [setCurrentUser, mParticleSignOutEvent, logUserOutOfThirdPartyServices, refreshCurrentUser]);

  const signInWithOtp = useCallback(
    async (param: { email: string } | { phoneNumber: string }) => {
      const sessionId = uuid();
      await createOtpMutation({
        variables: {
          input: {
            ...param,
            platform: platform(),
            sessionId,
          },
        },
      });
      // At this point we have created an OTP and we can show the OTP form
      // We also need to store the sessionId so that we can validate the OTP
      // If the user is on the modal auth flow we need to show the OTP form instead of navigating
      storeOtpCredentials({ ...param, sessionId });

      if (modalAuthIsOpen) {
        setModalAuthState(ModalAuthState.CHALLENGE);
      }

      return (navigateOnSuccessParams?: INavigateOnSuccessParams) => {
        navigate(routes.signUp, {
          state: {
            ...routeParams,
            ...navigateOnSuccessParams,
            showOtpForm: true,
            activeRouteIsSignIn: true,
          },
          replace: true,
        });
      };
    },
    [createOtpMutation, navigate, routeParams, modalAuthIsOpen, setModalAuthState]
  );

  const signInWithJwt = useCallback(
    async (email: string) => {
      LocalStorage.setItem(StorageKeys.LOGIN, email);
      await signInJwtMutation({
        variables: {
          input: {
            email,
            stage: welcomeEmailDomain(),
            platform: platform(),
          },
        },
      });
      return (navigateOnSuccessParams?: INavigateOnSuccessParams) => {
        navigate(routes.authChallengeJwt, { state: navigateOnSuccessParams });
      };
    },
    [navigate, signInJwtMutation]
  );

  const getOtpFlagValueForAttributes = useCallback(
    async (ldAttributes: DeepPartial<LDUser>) => {
      // omit nested empty/null values
      const attributes = cloneDeepWith(ldAttributes, value =>
        isPlainObject(value) ? pickBy(value, v => !!v) : !!value
      );

      const flagValue = await attemptGetUpdatedLdFlag(
        LaunchDarklyFlag.ENABLE_ONE_TIME_PASSWORD,
        attributes,
        OTPAuthDeliveryMethod.None
      );
      return flagValue;
    },
    [attemptGetUpdatedLdFlag]
  );

  const signInUsingEnabledMethod = useCallback(
    async ({
      email,
      phoneNumber,
      otpMethod,
    }: Pick<ISignIn, 'email' | 'phoneNumber'> & { otpMethod: OTPAuthDeliveryMethod }) => {
      const isEmailEnabled = isOTPEmailEnabled(otpMethod);
      const isSMSEnabled = isOTPSMSEnabled(otpMethod);

      if (isSMSEnabled && phoneNumber) {
        return signInWithOtp({ phoneNumber });
      }

      if (!email) {
        throw new Error('No email provided and SMS login disabled');
      }

      if (isEmailEnabled) {
        return signInWithOtp({ email });
      }

      return signInWithJwt(email);
    },
    [signInWithJwt, signInWithOtp]
  );

  const signIn = useCallback(
    async ({
      email,
      phoneNumber,
      navigateOnSuccess = true,
      navigateOnSuccessParams,
      authenticationMethod,
      socialAuthenticationType,
    }: ISignIn) => {
      const otpMethod: OTPAuthDeliveryMethod = await getOtpFlagValueForAttributes({
        email,
        custom: { phoneNumber },
      });

      setSignInOtpMethod(otpMethod);

      try {
        const navigateOnSuccessFn = await signInUsingEnabledMethod({
          email,
          phoneNumber,
          otpMethod,
        });
        mParticleSignInEvent({
          phase: SignInPhases.START,
          success: true,
          otpMethod,
          authenticationMethod,
          socialAuthenticationType,
        });
        if (navigateOnSuccess) {
          navigateOnSuccessFn(navigateOnSuccessParams);
        }
      } catch (error) {
        const createOtpError = enableUserNotFoundMaskAuthFlow
          ? GraphQLErrorCodes.CREATE_OTP_FAILED
          : GraphQLErrorCodes.AUTH_EMAIL_NOT_REGISTERED;
        // @ts-expect-error TS(2345) FIXME: Argument of type 'unknown' is not assignable to pa... Remove this comment to see the full error message
        const notRegisteredError = parseGraphQLErrorCodes(error)
          .map(err => err.errorCode)
          .includes(createOtpError);
        if (!notRegisteredError) {
          mParticleSignInEvent({
            phase: SignInPhases.START,
            success: false,
            // @ts-expect-error TS(2571) FIXME: Object is of type 'unknown'.
            message: error.message,
            otpMethod,
            authenticationMethod,
            socialAuthenticationType,
          });
        }
        // @ts-expect-error TS(2571) FIXME: Object is of type 'unknown'.
        error.code = SIGN_IN_FAIL;
        throw error;
      }
    },
    [
      enableUserNotFoundMaskAuthFlow,
      getOtpFlagValueForAttributes,
      mParticleSignInEvent,
      signInUsingEnabledMethod,
    ]
  );

  const signUp = useCallback(
    async (
      {
        email,
        name,
        dob,
        phoneNumber,
        country,
        wantsPromotionalEmails,
        zipcode,
        favoriteStores,
      }: ISignUp,
      signInOverride: (args: ISignIn) => Promise<void> = signIn,
      authenticationMethod?: string,
      socialAuthenticationType?: ProviderType
    ): Promise<ISignUpResult> => {
      let jwt;
      try {
        if (enableSignUpInBE) {
          const { data } = await signUpMutation({
            variables: {
              input: {
                country: country as IsoCountryCode,
                dob,
                name,
                phoneNumber,
                platform: platform(),
                stage: welcomeEmailDomain(),
                userName: email,
                wantsPromotionalEmails,
                zipcode,
                favoriteStores,
              },
            },
          });
          jwt = data?.signUp;
        } else {
          await cognitoSignUp({
            name,
            phoneNumber,
            username: email,
            country,
            wantsPromotionalEmails,
            dob,
          });
        }
      } catch (error) {
        mParticleSignUpEvent({
          success: false,
          // @ts-expect-error TS(2571) FIXME: Object is of type 'unknown'.
          message: error?.message,
          authenticationMethod,
          socialAuthenticationType,
        });

        throw error;
      }

      mParticleSignUpEvent({ success: true, authenticationMethod, socialAuthenticationType });

      await signInOverride({
        email,
        phoneNumber,
      });

      return { jwt };
    },
    [enableSignUpInBE, mParticleSignUpEvent, signIn, signUpMutation]
  );

  const validateLoginOtp = useCallback(
    async ({ otpCode }: any) => {
      try {
        let { email } = getStoredOtpCredentials() ?? {};
        const { phoneNumber, sessionId: storedSessionId } = getStoredOtpCredentials() ?? {};
        if (!storedSessionId) {
          throw new OtpValidationError('Missing sessionId');
        }

        const validateOtpResponse = await getSessionIdAndChallengeCodeOtp({
          email,
          phoneNumber,
          otpCode,
          sessionId: storedSessionId,
        });

        if (!email) {
          email = validateOtpResponse.email;
        }

        const { sessionId, code } = validateOtpResponse;

        const validateOtpMethod =
          signInOtpMethod && signInOtpMethod !== OTPAuthDeliveryMethod.None
            ? signInOtpMethod
            : await getOtpFlagValueForAttributes({
                email,
                custom: { phoneNumber },
              });

        const session = await cognitoValidateLogin({ username: email, code, sessionId });
        // This must be set __BEFORE__ the `setCurrentUser` state update.
        // we use this local storage value to know if we should skip fetching the user data from sanity.
        // by setting this ahead of time, we know that the user is valid and we can fetch user data.
        AuthStorage.setItem(
          StorageKeys.USER_AUTH_TOKEN,
          session?.getAccessToken().payload.username
        );

        mParticleSignInEvent({
          phase: SignInPhases.COMPLETE,
          success: true,
          otpMethod: validateOtpMethod,
          authenticationMethod: 'OTP',
          socialAuthenticationType: undefined,
        });
        setCurrentUser(session);
      } catch (error) {
        mParticleSignInEvent({
          phase: SignInPhases.COMPLETE,
          success: false,
          // @ts-expect-error TS(2571) FIXME: Object is of type 'unknown'.
          message: error.message,
          otpMethod: signInOtpMethod,
          authenticationMethod: 'OTP',
          socialAuthenticationType: undefined,
        });

        throw error;
      }
    },
    [
      signInOtpMethod,
      getSessionIdAndChallengeCodeOtp,
      getOtpFlagValueForAttributes,
      mParticleSignInEvent,
      setCurrentUser,
    ]
  );

  const validateLogin = useCallback(
    async ({ jwt, username }: IValidateLogin) => {
      setValidateLoginIsLoading(true);
      try {
        const { sessionId, code } = await getSessionIdAndChallengeCode(jwt);
        const session = await cognitoValidateLogin({ username, code, sessionId });
        mParticleSignInEvent({ phase: SignInPhases.COMPLETE, success: true });

        // This must be set __BEFORE__ the `setCurrentUser` state update.
        // we use this local storage value to know if we should skip fetching the user data from sanity.
        // by setting this ahead of time, we know that the user is valid and we can fetch user data.
        AuthStorage.setItem(
          StorageKeys.USER_AUTH_TOKEN,
          session?.getAccessToken().payload.username
        );
        setCurrentUser(session);
        // Add a marker when a user is successfull login to track unexpected sign outs
        LocalStorage.setItem(StorageKeys.USER_SIGNED_IN_SUCCESSFULLY, true);
        setValidateLoginIsLoading(false);
      } catch (error) {
        setValidateLoginIsLoading(false);

        mParticleSignInEvent({
          phase: SignInPhases.COMPLETE,
          success: false,
          // @ts-expect-error TS(2571) FIXME: Object is of type 'unknown'.
          message: error.message,
        });
        openErrorDialog({
          // @ts-expect-error TS(2322) FIXME: Type 'unknown' is not assignable to type 'Error | ... Remove this comment to see the full error message
          error,
          message: formatMessage({ id: 'authError' }),
          modalAppearanceEventMessage: 'Error: JWT Validation Failure',
        });
        throw error;
      }
    },
    [
      formatMessage,
      getSessionIdAndChallengeCode,
      mParticleSignInEvent,
      openErrorDialog,
      setCurrentUser,
    ]
  );

  const socialLogin = useCallback(
    async (input: ISocialLoginInput) => {
      try {
        const response = await socialLoginMutation({
          variables: {
            input,
          },
        });
        const session = await cognitoValidateLogin({
          username: response.data?.socialLogin?.email || '',
          code: response.data?.socialLogin?.challengeCode || '',
          sessionId: response.data?.socialLogin?.sessionId || '',
        });
        mParticleSignInEvent({
          phase: SignInPhases.COMPLETE,
          success: true,
          authenticationMethod: 'Social',
          socialAuthenticationType: input.providerType,
        });

        // This must be set __BEFORE__ the `setCurrentUser` state update.
        // we use this local storage value to know if we should skip fetching the user data from sanity.
        // by setting this ahead of time, we know that the user is valid and we can fetch user data.
        AuthStorage.setItem(
          StorageKeys.USER_AUTH_TOKEN,
          session?.getAccessToken().payload.username
        );
        setCurrentUser(session);
        // Add a marker when a user is successfull login to track unexpected sign outs
        LocalStorage.setItem(StorageKeys.USER_SIGNED_IN_SUCCESSFULLY, true);
      } catch (error) {
        mParticleSignInEvent({
          phase: SignInPhases.COMPLETE,
          success: false,
          // @ts-expect-error TS(2571) FIXME: Object is of type 'unknown'.
          message: error.message,
          authenticationMethod: 'Social',
          socialAuthenticationType: input.providerType,
        });
        throw error;
      }
    },
    [mParticleSignInEvent, setCurrentUser, socialLoginMutation]
  );

  return {
    authLoading:
      signInMutationLoading ||
      validateAuthMutationLoading ||
      createOtpMutationLoading ||
      validateOtpMutationLoading ||
      socialLoginMutationLoading ||
      validateLoginIsLoading,
    originLocation,
    setOriginLoc,
    signIn,
    signOut,
    signUp,
    socialLogin,
    validateLogin,
    validateLoginOtp,
  };
};
